import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Grid, Button } from '@material-ui/core'
import { yupResolver } from '@hookform/resolvers'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { format } from 'date-fns'

import { Form, SelectField, TextField, DatePickerField, DocumentItem, SafeDropzone, AddressLookup, PhoneField } from '../common'
import { RootState, setAgentFormPayload } from 'src/lib/store'
import { STATES, DELIVERY_METHODS } from 'src/lib/constants'
import { AgentInfo } from 'src/lib/types'
import { genAgentFormValidationSchema, genAgentFormDefaultValues } from 'src/lib/helpers'

export const AgentInfoForm: React.FC = () => {
  const payloadState = useSelector((state: RootState) => state.payload.data)
  const dispatch = useDispatch()
  const history = useHistory()
  const [selectedFiles, setSelectedFiles] = useState<any>(payloadState.formAttachments)
  const validationSchema = genAgentFormValidationSchema()

  const { control, errors, watch, handleSubmit, setValue } = useForm<AgentInfo>({
    resolver: yupResolver(validationSchema),
    defaultValues: genAgentFormDefaultValues(payloadState)
  })

  const watchFields = watch()

  const back = async () => {
    if (watchFields.dob !== '') dispatch(setAgentFormPayload({ ...watchFields, dob: format(new Date(watchFields.dob), 'MM/dd/yyyy'), documents: selectedFiles }))
    if (watchFields.dob === '') dispatch(setAgentFormPayload({ ...watchFields, dob: '', documents: selectedFiles }))
    history.goBack()
  }

  const onSubmit = (payload: AgentInfo) => {
    dispatch(setAgentFormPayload({ ...payload, dob: format(new Date(payload.dob), 'MM/dd/yyyy'), documents: selectedFiles }))
    history.push('/consumer-info')
  }

  return (
    <Form title="Complete the section about the agent." handleSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SelectField control={control} name="deliveryMethod" label="Communication Preference" items={DELIVERY_METHODS} error={errors.deliveryMethod} testId="deliveryMethod" />
        </Grid>
        <Grid item sm={6} />
        <Grid item xs={12} sm={4}>
          <TextField name="firstName" label="First Name" control={control} error={errors.firstName} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="middleName" label="Middle Name" control={control} error={errors.middleName} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField name="lastName" label="Last Name"control={control} error={errors.lastName} />
        </Grid>
        <Grid item xs={12} sm={1}>
          <TextField name="suffix" label="Suffix" control={control} error={errors.suffix} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="email" label="Email" control={control} error={errors.email} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PhoneField name="phone" control={control} setField={setValue} fields={watchFields} error={errors.phone} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerField control={control} label="Date of Birth" error={errors.dob} />
        </Grid>
        <Grid item xs={12}>
          <AddressLookup name="addressLine1" control={control} fields={watchFields} label="Mailing Address Line 1" setField={setValue} error={errors.addressLine1} />
        </Grid>
        <Grid item xs={12}>
          <TextField name="addressLine2" label="Address Line 2 (optional)" control={control} error={errors.addressLine2} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="city" label="City" control={control} error={errors.city} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SelectField control={control} name="state" id="state" defaultValue={''} label="State" items={STATES} testId="states" error={errors.state} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField name="zip" type="phone" label="ZIP Code" control={control} inputProps={{maxLength: 10 }} error={errors.zip} />
        </Grid>
        <Grid item xs={12} md={6} className="mt-2">
          <SafeDropzone setSelectedFiles={setSelectedFiles} selectedFiles={selectedFiles} />
        </Grid>
        {payloadState.formAttachments && (
          <Grid item xs={12} md={6}>
            {payloadState.formAttachments.map((file, i) => (
              <DocumentItem file={file} setSelectedFiles={setSelectedFiles} key={file.name + i} />
            ))}
          </Grid>
        )}
        <Grid item xs={6}>
          <Button className="mt-4 w-100" color="primary" variant="contained" onClick={() => back()}>
            Back
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button className="mt-4 w-100" color="primary" variant="contained" type="submit">
            Next
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}
